import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/integrations/supabase/client';

export const MASTERCLASS_RATINGS_QUERY_KEYS = {
  byCertificationProgress: (certificationProgressId: string) =>
    ['masterclass_ratings', 'certification_progress', certificationProgressId] as const,
  byUserAndCase: (userId: string, caseId: string) =>
    ['masterclass_ratings', 'user', userId, 'case', caseId] as const,
  byCase: (caseId: string) => ['masterclass_ratings', 'case', caseId] as const,
};

export const useMasterclassRatingByCertificationProgress = (
  certificationProgressId: string | null
) => {
  return useQuery({
    queryKey: MASTERCLASS_RATINGS_QUERY_KEYS.byCertificationProgress(certificationProgressId || ''),
    queryFn: async () => {
      if (!certificationProgressId) throw new Error('Certification Progress ID is required');

      const { data, error } = await supabase
        .from('masterclass_ratings')
        .select('id')
        .eq('certification_progress_id', certificationProgressId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!certificationProgressId,
  });
};

export const useMasterclassRatingByUserAndCase = (userId: string | null, caseId: string | null) => {
  return useQuery({
    queryKey: MASTERCLASS_RATINGS_QUERY_KEYS.byUserAndCase(userId || '', caseId || ''),
    queryFn: async () => {
      if (!userId || !caseId) throw new Error('User ID and Case ID are required');

      const { data, error } = await supabase
        .from('masterclass_ratings')
        .select('id')
        .eq('user_id', userId)
        .eq('case_id', caseId)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!userId && !!caseId,
  });
};

export const useInsertMasterclassRating = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      caseId,
      certificationProgressId,
      applicationRating,
      masterclassRating,
    }: {
      userId: string;
      caseId: string;
      certificationProgressId: string;
      applicationRating: number;
      masterclassRating: number;
    }) => {
      const { data, error } = await supabase
        .from('masterclass_ratings')
        .insert({
          user_id: userId,
          case_id: caseId,
          certification_progress_id: certificationProgressId,
          application_rating: applicationRating,
          masterclass_rating: masterclassRating,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({
        queryKey: MASTERCLASS_RATINGS_QUERY_KEYS.byCertificationProgress(
          data.certification_progress_id
        ),
      });
      void queryClient.invalidateQueries({
        queryKey: MASTERCLASS_RATINGS_QUERY_KEYS.byUserAndCase(data.user_id, data.case_id),
      });
      void queryClient.invalidateQueries({
        queryKey: MASTERCLASS_RATINGS_QUERY_KEYS.byCase(data.case_id),
      });
    },
  });
};
